import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  LinearProgress,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TablePagination,
  Typography,
} from '@mui/material';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { EmptyState, FilterChipBarV2 } from '../../../components/v2';
import BaseService from '../../../services/BaseService';
import * as actionsNotificacao from '../../../store/notificacao/actions';
import * as selectorsNotificacao from '../../../store/notificacao/reducer';
import * as actionsRelatorioJob from '../../../store/relatorioJob/actions';
import * as selectorsRelatorioJob from '../../../store/relatorioJob/reducer';
import * as actionsToast from '../../../store/toast/actions';
import {
  NOTIFICACAO_LEITURAS,
  NOTIFICACAO_MODULOS,
  NOTIFICACAO_PAGE_SIZE,
  NOTIFICACAO_SITUACOES,
  buildNotificacaoFilter,
  getRelatorioJobFromNotificacao,
  canNavigateNotificacao,
  isNotificacaoLida,
  isRelatorioGerando,
  leituraToLidaFilter,
  parseNotificacaoPage,
} from '../../../helpers/notificacao';
import NotificacaoItem from './NotificacaoItem';

const LIST_REFRESH_MS = 5000;

const toggleChipValue = (current, next) => (current === next ? '' : next);

const NotificacaoListSkeleton = ({ rows = 5 }) => (
  <Box
    sx={{
      width: '100%',
      border: (theme) => `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      overflow: 'hidden',
    }}
  >
    {Array.from({ length: rows }).map((_, index) => (
      <Stack
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        direction="row"
        spacing={1.5}
        alignItems="flex-start"
        sx={{
          px: 1.5,
          py: 1.5,
          borderBottom: (theme) => (
            index === rows - 1 ? 0 : `1px solid ${theme.palette.divider}`
          ),
        }}
      >
        <Skeleton
          variant="rectangular"
          width={18}
          height={18}
          sx={{ mt: 0.75, borderRadius: 0.5, flexShrink: 0 }}
        />
        <Skeleton
          variant="circular"
          width={36}
          height={36}
          sx={{ flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton width="55%" height={18} />
          <Skeleton width="80%" height={14} sx={{ mt: 0.75 }} />
          <Skeleton width="35%" height={12} sx={{ mt: 0.75 }} />
        </Box>
      </Stack>
    ))}
  </Box>
);

NotificacaoListSkeleton.propTypes = {
  rows: PropTypes.number,
};

NotificacaoListSkeleton.defaultProps = {
  rows: 5,
};

const NotificacoesV2 = ({
  revision,
  downloadingId,
  markNotificacaoRead,
  markNotificacoesLote,
  downloadRelatorioJob,
  messageError,
  history,
}) => {
  const [modulo, setModulo] = useState('');
  const [tipo, setTipo] = useState('');
  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const [leitura, setLeitura] = useState('');
  const [verArquivadas, setVerArquivadas] = useState(false);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const loadRequestIdRef = useRef(0);

  const hasGerando = useMemo(
    () => rows.some((row) => isRelatorioGerando(row)),
    [rows]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => setBuscaDebounced(busca), 300);
    return () => window.clearTimeout(timer);
  }, [busca]);

  useEffect(() => {
    setPage(0);
  }, [buscaDebounced]);

  const apiFilter = useMemo(() => buildNotificacaoFilter({
    tipo: tipo || undefined,
    modulo: modulo || undefined,
    visualizada: leituraToLidaFilter(leitura),
    q: buscaDebounced,
    arquivada: verArquivadas,
  }), [tipo, modulo, leitura, buscaDebounced, verArquivadas]);

  const load = useCallback(async ({ silent = false } = {}) => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    if (!silent) setLoading(true);
    try {
      const response = await BaseService.post({
        url: `notificacoes/filter?p=${page}&s=${NOTIFICACAO_PAGE_SIZE}`,
        data: apiFilter,
      });
      if (requestId !== loadRequestIdRef.current) return;
      const parsed = parseNotificacaoPage(response.data);
      setRows(parsed.rows);
      setTotal(parsed.totalRows);
    } catch (error) {
      if (requestId !== loadRequestIdRef.current) return;
      if (!silent) {
        setRows([]);
        setTotal(0);
        messageError({ message: 'Erro ao carregar notificações.' });
      }
    }
    if (requestId === loadRequestIdRef.current && !silent) setLoading(false);
  }, [page, apiFilter, messageError]);

  useEffect(() => {
    load();
  }, [load, revision]);

  useEffect(() => {
    if (!hasGerando) return undefined;
    const timer = window.setInterval(() => load({ silent: true }), LIST_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [hasGerando, load]);

  useEffect(() => {
    setSelectedIds([]);
  }, [tipo, modulo, buscaDebounced, leitura, verArquivadas, page]);

  const visibleIds = rows.map((row) => row.id);
  const selectedEntries = rows.filter((row) => (
    selectedIds.some((id) => String(id) === String(row.id))
  ));
  const selectedVisible = selectedEntries.map((row) => row.id);
  const allSelected = visibleIds.length > 0 && selectedVisible.length === visibleIds.length;
  const someSelected = selectedVisible.length > 0 && !allSelected;
  const selectedLidas = selectedEntries.filter((row) => isNotificacaoLida(row)).length;
  const selectedNaoLidas = selectedEntries.length - selectedLidas;
  const selecaoMista = selectedEntries.length > 0 && selectedLidas > 0 && selectedNaoLidas > 0;
  const selecaoSoLidas = selectedEntries.length > 0 && selectedNaoLidas === 0;
  const selecaoSoNaoLidas = selectedEntries.length > 0 && selectedLidas === 0;

  const openItem = (item) => {
    if (!isNotificacaoLida(item)) markNotificacaoRead(item.id, true);
    if (canNavigateNotificacao(item)) {
      history.push(item.referenciaPath);
    }
  };

  const applyLote = ({ lida, arquivada }) => {
    if (selectedVisible.length === 0) return;
    markNotificacoesLote({ ids: selectedVisible, lida, arquivada });
    setSelectedIds([]);
  };

  const toggleSelected = (id) => {
    setSelectedIds((current) => {
      const exists = current.some((value) => String(value) === String(id));
      if (exists) return current.filter((value) => String(value) !== String(id));
      return [...current, id];
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(visibleIds);
  };

  return (
    <Box>
      <Stack
        spacing={3}
        sx={{
          maxWidth: 1300,
          mx: 'auto',
          width: '100%',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4">Notificações</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {total}
              {' '}
              {total === 1 ? 'notificação' : 'notificações'}
              {verArquivadas ? ' arquivadas' : ''}
            </Typography>
          </Box>
          <Button
            variant={verArquivadas ? 'contained' : 'text'}
            startIcon={verArquivadas ? <UnarchiveOutlinedIcon /> : <ArchiveOutlinedIcon />}
            onClick={() => {
              setVerArquivadas((current) => !current);
              setPage(0);
            }}
          >
            {verArquivadas ? 'Voltar à inbox' : 'Arquivadas'}
          </Button>
        </Stack>

        <Stack spacing={2}>
          <FilterChipBarV2
            searchLabel="Buscar"
            searchValue={busca}
            onSearchChange={setBusca}
            searchPlaceholder="Título, mensagem, módulo ou arquivo"
            searchContainerSx={{
              width: { xs: '100%', md: 380 },
              maxWidth: { md: 440 },
            }}
            chipGroupsLayout="search-row"
            showCount={false}
            filterActive={Boolean(busca || tipo || leitura)}
            onClear={() => {
              setBusca('');
              setBuscaDebounced('');
              setTipo('');
              setLeitura('');
              setPage(0);
            }}
            chipGroups={[
              {
                label: 'Situação',
                chips: NOTIFICACAO_SITUACOES.map((option) => ({
                  id: option.id,
                  label: option.label,
                  selected: tipo === option.id,
                  onClick: () => {
                    setTipo((current) => toggleChipValue(current, option.id));
                    setPage(0);
                  },
                })),
              },
              {
                label: 'Leitura',
                chips: NOTIFICACAO_LEITURAS.map((option) => ({
                  id: option.id,
                  label: option.label,
                  selected: leitura === option.id,
                  onClick: () => {
                    setLeitura((current) => toggleChipValue(current, option.id));
                    setPage(0);
                  },
                })),
              },
            ]}
          />

          <Box sx={{ position: 'relative' }}>
            <Tabs
              value={modulo}
              onChange={(_, next) => {
                setModulo(next);
                setPage(0);
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 40,
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  minHeight: 40,
                  textTransform: 'none',
                  fontWeight: 600,
                },
              }}
            >
              {NOTIFICACAO_MODULOS.map((option) => (
                <Tab
                  key={option.id || 'all'}
                  value={option.id}
                  label={option.label}
                />
              ))}
            </Tabs>
            {loading && (
              <LinearProgress
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 2,
                }}
              />
            )}
          </Box>
        </Stack>

        {loading && rows.length === 0 && (
          <NotificacaoListSkeleton />
        )}

        {!loading && rows.length === 0 && (
          <EmptyState
            title={verArquivadas ? 'Nenhuma arquivada' : 'Nenhuma notificação'}
            message={verArquivadas
              ? 'Itens arquivados aparecem aqui. Volte à inbox para continuar.'
              : 'Quando houver uma ação pendente ou um aviso, ele aparece aqui.'}
          />
        )}

        {rows.length > 0 && (
          <Box
            sx={{
              width: '100%',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 1.5,
                py: 0.75,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                bgcolor: selectedVisible.length > 0 ? 'action.selected' : 'transparent',
              }}
            >
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={toggleSelectAll}
                inputProps={{ 'aria-label': 'Selecionar todas' }}
              />
              {selectedVisible.length > 0 ? (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mr: 1 }}
                  >
                    {selectedVisible.length}
                    {' '}
                    selecionada
                    {selectedVisible.length === 1 ? '' : 's'}
                  </Typography>
                  {!selecaoMista && selecaoSoNaoLidas && (
                    <Button
                      size="small"
                      startIcon={<MarkEmailReadOutlinedIcon />}
                      onClick={() => applyLote({ lida: true })}
                    >
                      Marcar como lida
                    </Button>
                  )}
                  {!selecaoMista && selecaoSoLidas && (
                    <Button
                      size="small"
                      startIcon={<MarkEmailUnreadOutlinedIcon />}
                      onClick={() => applyLote({ lida: false })}
                    >
                      Marcar como não lida
                    </Button>
                  )}
                  <Button
                    size="small"
                    startIcon={verArquivadas ? <UnarchiveOutlinedIcon /> : <ArchiveOutlinedIcon />}
                    onClick={() => applyLote({ arquivada: !verArquivadas })}
                  >
                    {verArquivadas ? 'Desarquivar' : 'Arquivar'}
                  </Button>
                </>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  {verArquivadas
                    ? 'Selecione para desarquivar ou alterar leitura'
                    : 'Selecione para marcar leitura ou arquivar'}
                </Typography>
              )}
            </Stack>

            {rows.map((row) => {
              const job = getRelatorioJobFromNotificacao(row);
              const downloading = Boolean(
                job && downloadingId != null && String(downloadingId) === String(job.id)
              );
              const checked = selectedVisible.some((id) => String(id) === String(row.id));
              return (
                <Box
                  key={row.id}
                  sx={{
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    '&:last-of-type': { borderBottom: 0 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      gap: 0.5,
                      alignItems: 'center',
                      px: 1.5,
                      py: 1.5,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={() => toggleSelected(row.id)}
                      onClick={(event) => event.stopPropagation()}
                      inputProps={{ 'aria-label': `Selecionar ${row.titulo}` }}
                      sx={{ flexShrink: 0 }}
                    />
                    <Box
                      role="button"
                      tabIndex={0}
                      aria-label={row.titulo || 'Abrir notificação'}
                      onClick={() => openItem(row)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openItem(row);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        flex: 1,
                        minWidth: 0,
                        gap: 1,
                        alignItems: 'center',
                        textAlign: 'left',
                        cursor: 'pointer',
                        borderRadius: 1,
                        outline: 'none',
                        '&:focus-visible': {
                          boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
                        },
                      }}
                    >
                      <NotificacaoItem
                        item={row}
                        showMessage
                        fileBeside
                        downloading={downloading}
                        onDownload={(fileJob) => {
                          downloadRelatorioJob(fileJob);
                          if (!isNotificacaoLida(row)) markNotificacaoRead(row.id, true);
                        }}
                      />
                      {canNavigateNotificacao(row) && (
                        <ChevronRightIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {total > NOTIFICACAO_PAGE_SIZE && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={NOTIFICACAO_PAGE_SIZE}
            rowsPerPageOptions={[NOTIFICACAO_PAGE_SIZE]}
            labelRowsPerPage=""
          />
        )}
      </Stack>
    </Box>
  );
};

NotificacoesV2.propTypes = {
  revision: PropTypes.number,
  downloadingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  markNotificacaoRead: PropTypes.func.isRequired,
  markNotificacoesLote: PropTypes.func.isRequired,
  downloadRelatorioJob: PropTypes.func.isRequired,
  messageError: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
};

NotificacoesV2.defaultProps = {
  revision: 0,
  downloadingId: null,
};

const mapStateToProps = (state) => ({
  revision: selectorsNotificacao.getNotificacaoRevision(state),
  downloadingId: selectorsRelatorioJob.getRelatorioJobDownloadingId(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  markNotificacaoRead: actionsNotificacao.markNotificacaoRead,
  markNotificacoesLote: actionsNotificacao.markNotificacoesLote,
  downloadRelatorioJob: actionsRelatorioJob.downloadRelatorioJob,
  messageError: actionsToast.messageError,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(NotificacoesV2));
